import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LifeBuoy,
  HelpCircle,
  Search,
  Mail,
  MessageCircle,
  CalendarClock,
  ShieldCheck,
  Wallet,
  CarFront,
  UserCog,
  Bell,
  PhoneCall,
  Truck,
  ArrowRight,
} from "lucide-react";
import "./MarketingPage.css";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL } from "../../config/company";

const TOPICS = [
  { key: "all",       label: "Tous",                icon: <LifeBuoy size={14} /> },
  { key: "account",   label: "Compte & inscription", icon: <UserCog size={14} /> },
  { key: "rdv",       label: "Rendez-vous",         icon: <CalendarClock size={14} /> },
  { key: "abo",       label: "Abonnement",          icon: <Wallet size={14} /> },
  { key: "missions",  label: "ProMeet Missions",    icon: <Truck size={14} /> },
  { key: "security",  label: "Sécurité",            icon: <ShieldCheck size={14} /> },
  { key: "notif",     label: "Notifications",       icon: <Bell size={14} /> },
];

const FAQS = [
  {
    topic: "account",
    q: "Comment créer un compte professionnel ?",
    a: "Cliquez sur « Inscription » puis « Inscription pro », renseignez vos informations et téléversez vos justificatifs (KBIS, pièce d'identité, justificatif de domicile). Notre équipe vérifie votre dossier sous 24-48h ouvrées.",
  },
  {
    topic: "account",
    q: "Je n'ai pas reçu mon e-mail de vérification, que faire ?",
    a: "Vérifiez vos spams puis utilisez le bouton « Renvoyer le code » sur la page de vérification. Si le souci persiste, contactez-nous depuis cette page.",
  },
  {
    topic: "rdv",
    q: "Comment réserver un rendez-vous chez un pro ?",
    a: "Recherchez un professionnel via la barre de recherche, choisissez une activité et une disponibilité, puis confirmez. Vous recevez immédiatement un récapitulatif par e-mail.",
  },
  {
    topic: "rdv",
    q: "Puis-je annuler ou décaler un rendez-vous ?",
    a: "Oui, depuis « Mes rendez-vous » dans votre espace. Selon les conditions du pro, l'annulation peut être gratuite jusqu'à un certain délai avant le RDV.",
  },
  {
    topic: "abo",
    q: "Quelle est la différence entre les abonnements basic, standard et premium ?",
    a: "Le plan basic permet de gérer un profil et les RDV. Standard ajoute la gestion des documents et factures. Premium permet jusqu'à 5 profils et fonctionnalités avancées. Voir la page « Abonnement » pour le détail.",
  },
  {
    topic: "abo",
    q: "Comment changer ou annuler mon abonnement ?",
    a: "Allez dans « Abonnement » depuis votre menu — vous pouvez changer de plan ou résilier à tout moment, sans engagement.",
  },
  {
    topic: "missions",
    q: "Qui peut poster une mission sur ProMeet Missions ?",
    a: "Toute concession, garage, négociant ou société automobile inscrit sur la plateforme peut poster une mission en moins de 30 secondes.",
  },
  {
    topic: "missions",
    q: "Comment postuler à une mission en tant que prestataire ?",
    a: "Ouvrez une mission depuis la liste « Missions disponibles » et cliquez sur « Postuler ». Vos informations pro sont pré-remplies — vous saisissez prix, délai et un message court.",
  },
  {
    topic: "missions",
    q: "Comment fonctionne la commission de la plateforme ?",
    a: "La plateforme applique 10 % de commission sur chaque mission. Exemple : pour une mission à 500 €, vous percevez 450 € (la commission de 50 € est affichée en clair lors de la candidature).",
  },
  {
    topic: "missions",
    q: "Puis-je envoyer des photos et la position à mon client ?",
    a: "Oui, depuis la conversation de la mission — joignez photos, partagez votre position GPS, et utilisez la section « Photos / Validation » pour les photos avant / pendant / après.",
  },
  {
    topic: "security",
    q: "Comment sont vérifiés les professionnels ?",
    a: "Tous les pros transmettent leurs documents officiels (KBIS, identité, justificatif de domicile). Une équipe valide chaque dossier avant publication des annonces.",
  },
  {
    topic: "security",
    q: "Mes données sont-elles protégées ?",
    a: "Oui. Hébergement en Europe, chiffrement des mots de passe, accès sécurisé par token JWT. Voir nos « Conditions générales » pour les détails.",
  },
  {
    topic: "notif",
    q: "Puis-je désactiver les notifications ?",
    a: "Oui, depuis « Paramètres ». Vous pouvez choisir quelles notifications recevoir par e-mail et, prochainement, par push & SMS.",
  },
];

export default function HelpPage() {
  const [topic, setTopic] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      const matchTopic = topic === "all" || f.topic === topic;
      const matchQuery = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return matchTopic && matchQuery;
    });
  }, [topic, query]);

  return (
    <div className="mkt-shell">
      <div className="mkt-container">
        {/* ========= HERO ========= */}
        <section className="mkt-hero">
          <div className="mkt-hero__inner">
            <div className="mkt-hero__icon">
              <LifeBuoy size={36} strokeWidth={2.2} />
            </div>
            <div className="mkt-hero__copy">
              <h1>Besoin d'aide ?</h1>
              <p>
                Trouvez en quelques secondes la réponse à vos questions, ou contactez
                directement notre équipe — on répond sous 24h ouvrées.
              </p>
              <div className="mkt-hero__actions">
                <a className="mkt-btn mkt-btn--primary" href={`mailto:${CONTACT_EMAIL}`}>
                  <Mail size={16} /> Écrire au support
                </a>
                <a className="mkt-btn mkt-btn--ghost" href={`tel:${CONTACT_PHONE_TEL}`}>
                  <PhoneCall size={16} /> {CONTACT_PHONE}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ========= Raccourcis ========= */}
        <h2 className="mkt-section-title"><HelpCircle size={20} /> Aide rapide</h2>
        <section className="mkt-grid-2">
          <Link to="/pro/calendar" className="mkt-feature" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="mkt-feature__icon"><CalendarClock size={22} /></div>
            <div>
              <h3>Gérer mes rendez-vous</h3>
              <p>Voir, modifier ou annuler vos RDV en cours.</p>
            </div>
          </Link>
          <Link to="/missions/dashboard" className="mkt-feature mkt-feature--info" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="mkt-feature__icon"><Truck size={22} /></div>
            <div>
              <h3>Mes missions</h3>
              <p>Accédez au tableau de bord ProMeet Missions.</p>
            </div>
          </Link>
          <Link to="/billing/subscription" className="mkt-feature mkt-feature--violet" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="mkt-feature__icon"><Wallet size={22} /></div>
            <div>
              <h3>Mon abonnement</h3>
              <p>Changer de plan, voir les factures, gérer le paiement.</p>
            </div>
          </Link>
          <Link to="/pro/settings" className="mkt-feature mkt-feature--success" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="mkt-feature__icon"><UserCog size={22} /></div>
            <div>
              <h3>Mes paramètres</h3>
              <p>E-mail, mot de passe, profil, notifications.</p>
            </div>
          </Link>
        </section>

        {/* ========= FAQ ========= */}
        <h2 className="mkt-section-title"><HelpCircle size={20} /> Questions fréquentes</h2>

        <div className="mkt-card" style={{ padding: 16 }}>
          {/* Search */}
          <div style={{ position: "relative", marginBottom: 12 }}>
            <Search size={16}
                    style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            <input
              type="text"
              placeholder="Rechercher une question…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                width: "100%", padding: "12px 14px 12px 38px",
                border: "1px solid var(--border-strong)",
                borderRadius: 12, fontSize: 15,
                background: "#fff",
              }}
            />
          </div>

          {/* Topic filters */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {TOPICS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTopic(t.key)}
                className="mkt-btn"
                style={{
                  padding: "8px 14px",
                  background: topic === t.key
                    ? "linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))"
                    : "#fff",
                  color: topic === t.key ? "#fff" : "var(--espresso)",
                  border: topic === t.key ? "1px solid transparent" : "1px solid var(--border-strong)",
                  boxShadow: topic === t.key ? "var(--shadow-brand)" : "none",
                  minHeight: 40,
                  fontSize: 13,
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div className="mkt-faq">
            {filtered.length === 0 ? (
              <div
                style={{
                  background: "var(--cream-warm)",
                  border: "1px dashed var(--border)",
                  borderRadius: 14,
                  padding: 24, textAlign: "center", color: "var(--text-muted)",
                }}
              >
                Aucune réponse pour cette recherche. Contactez-nous directement par e-mail.
              </div>
            ) : (
              filtered.map((f, i) => (
                <details key={i}>
                  <summary>
                    <span className="mkt-faq-icon">
                      {f.topic === "rdv" && <CalendarClock size={16} />}
                      {f.topic === "abo" && <Wallet size={16} />}
                      {f.topic === "missions" && <CarFront size={16} />}
                      {f.topic === "security" && <ShieldCheck size={16} />}
                      {f.topic === "notif" && <Bell size={16} />}
                      {f.topic === "account" && <UserCog size={16} />}
                    </span>
                    {f.q}
                  </summary>
                  <p>{f.a}</p>
                </details>
              ))
            )}
          </div>
        </div>

        {/* ========= Contact ========= */}
        <h2 className="mkt-section-title"><MessageCircle size={20} /> Toujours pas de réponse ?</h2>
        <section className="mkt-grid-2">
          <a className="mkt-feature" href={`mailto:${CONTACT_EMAIL}`} style={{ textDecoration: "none", color: "inherit" }}>
            <div className="mkt-feature__icon"><Mail size={22} /></div>
            <div>
              <h3>Par e-mail</h3>
              <p>{CONTACT_EMAIL} — réponse sous 24h ouvrées.</p>
            </div>
          </a>
          <a className="mkt-feature mkt-feature--info" href={`tel:${CONTACT_PHONE_TEL}`} style={{ textDecoration: "none", color: "inherit" }}>
            <div className="mkt-feature__icon"><PhoneCall size={22} /></div>
            <div>
              <h3>Par téléphone</h3>
              <p>{CONTACT_PHONE} — du lundi au vendredi, 9h - 18h.</p>
            </div>
          </a>
        </section>

        {/* ========= CTA ========= */}
        <section className="mkt-cta">
          <div>
            <h3>Vous êtes pro et souhaitez une démo ?</h3>
            <p>Notre équipe vous accompagne pour démarrer en 15 minutes.</p>
          </div>
          <div className="mkt-cta__actions">
            <a className="mkt-btn mkt-btn--primary" href={`mailto:${CONTACT_EMAIL}?subject=Demande%20de%20démo`}>
              Demander une démo <ArrowRight size={14} />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
